// Firm feed — Aaron's request, 2026-08-29.
//
// What the firm has been doing, newest first, grouped by day. Meant to
// be glanced at, so it reads as prose rather than as a table: the audit
// log already exists for anyone who needs to interrogate the record.
//
// Deliberately curated on the server — PHI reads, credential events and
// sync churn never reach this page. Worth knowing when reading it: a
// quiet day here means a quiet day of case work, not that nothing
// happened in the system.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const WINDOWS = [
  { days: 7, label: "7 days" },
  { days: 14, label: "14 days" },
  { days: 30, label: "30 days" },
];

// Turns a local date key into something a person reads without decoding.
function dayLabel(iso) {
  const today = new Date();
  const d = new Date(iso + "T12:00:00");
  const days = Math.round((today.setHours(12, 0, 0, 0) - d.getTime()) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "long", day: "numeric", month: "long",
  });
}

// Regroup by the VIEWER's date, not the server's.
//
// The server groups in UTC, which puts anything worked after 7pm
// Central into the following day — so a paralegal's Friday evening
// shows up under Saturday, and "Today" can read as "Tomorrow". The
// browser is the only party that knows the reader's timezone, so the
// grouping belongs here. Item times are formatted locally for the same
// reason.
function regroupLocal(serverDays) {
  const byDate = new Map();
  for (const day of serverDays || []) {
    for (const item of day.items || []) {
      const at = item.at ? new Date(item.at) : null;
      const key = at
        ? `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, "0")}`
          + `-${String(at.getDate()).padStart(2, "0")}`
        : day.date;
      const time = at
        ? at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
        : item.time;
      if (!byDate.has(key)) byDate.set(key, []);
      byDate.get(key).push({ ...item, time });
    }
  }
  return [...byDate.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([date, items]) => ({
      date,
      count: items.length,
      items: items.sort((a, b) => (a.at < b.at ? 1 : -1)),
    }));
}

export function FirmFeedPage({ onMatterClick }) {
  const [feed, setFeed] = useState(null);
  const [days, setDays] = useState(14);
  const [person, setPerson] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const qs = new URLSearchParams({ days: String(days) });
      if (person) qs.set("user_name", person);
      setFeed(await apiFetch(`/api/firm-feed?${qs.toString()}`));
    } catch (e) {
      setError(e.message);
      setFeed({ days: [], total: 0, people: [] });
    }
  }, [days, person]);

  useEffect(() => { load(); }, [load]);

  if (feed === null) {
    return React.createElement("div", {
      style: { display: "flex", gap: "8px", alignItems: "center", padding: "40px 0" },
    },
      React.createElement(Spinner, { size: 16 }),
      React.createElement("span", { style: { fontSize: "13px", color: C.textMuted } },
        "Loading the firm's activity…"));
  }

  return React.createElement("div", { style: { padding: "32px 40px", maxWidth: "820px" } },
    React.createElement("h1", {
      style: { fontFamily: "'Playfair Display', serif", fontSize: "32px",
               color: C.navy, marginBottom: "6px" },
    }, "Firm feed"),
    React.createElement("p", {
      style: { fontSize: "14px", color: C.textMuted, marginBottom: "20px",
               lineHeight: 1.55 },
    }, "Case work across every matter, newest first. Record access and "
     + "system housekeeping are left out — the audit log has those."),

    error && React.createElement("div", {
      style: { fontSize: "13px", color: C.textDark, background: C.warmWhite,
               border: `1px solid ${C.warmGray}`, borderRadius: "8px",
               padding: "11px 14px", marginBottom: "16px" },
    }, error),

    // Window + person filters
    React.createElement("div", {
      className: "wrap-row",
      style: { display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center",
               marginBottom: "20px" },
    },
      ...WINDOWS.map((w) => React.createElement("button", {
        key: w.days,
        onClick: () => setDays(w.days),
        style: days === w.days ? primaryBtn : quietBtn,
      }, w.label)),
      React.createElement("div", { style: { flex: 1 } }),
      person && React.createElement("button", {
        onClick: () => setPerson(null), style: quietBtn,
      }, `Showing ${person} — clear`),
    ),

    // Who has been active. Doubles as the person filter.
    feed.people && feed.people.length > 1 && React.createElement("div", {
      style: { display: "flex", gap: "7px", flexWrap: "wrap", marginBottom: "22px" },
    },
      ...feed.people.slice(0, 8).map((p) => React.createElement("button", {
        key: p.name,
        onClick: () => setPerson(p.name === person ? null : p.name),
        style: {
          ...(p.name === person ? primaryBtn : quietBtn),
          fontWeight: 600,
        },
      }, `${p.name} · ${p.count}`)),
    ),

    feed.total === 0
      ? React.createElement("div", {
          style: { background: "#fff", border: `1px solid ${C.warmGray}`,
                   borderRadius: "12px", padding: "28px", fontSize: "14px",
                   color: C.textMuted, lineHeight: 1.6 },
        }, person
            ? `No case work recorded for ${person} in the last ${feed.window_days} days.`
            : `No case work recorded in the last ${feed.window_days} days. `
              + "Activity appears here as documents are generated, demands go "
              + "out, and matters move.")
      : React.createElement("div", null,
          ...regroupLocal(feed.days).map((day) => React.createElement("div", {
            key: day.date,
            style: { marginBottom: "26px" },
          },
            React.createElement("div", {
              style: { display: "flex", alignItems: "baseline", gap: "10px",
                       marginBottom: "10px" },
            },
              React.createElement("h2", {
                style: { fontSize: "13px", color: C.navy, fontWeight: 700,
                         textTransform: "uppercase", letterSpacing: "0.06em",
                         margin: 0 },
              }, dayLabel(day.date)),
              React.createElement("div", {
                style: { flex: 1, height: "1px", background: C.warmGray },
              }),
              React.createElement("span", {
                style: { fontSize: "11px", color: C.textLight },
              }, `${day.count} item${day.count === 1 ? "" : "s"}`),
            ),

            React.createElement("div", {
              style: { background: "#fff", border: `1px solid ${C.warmGray}`,
                       borderRadius: "10px", overflow: "hidden" },
            },
              ...day.items.map((it, i) => React.createElement("div", {
                key: it.id,
                className: "stack-row",
                style: {
                  display: "flex", gap: "12px", alignItems: "baseline",
                  padding: "11px 15px",
                  borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}55`,
                },
              },
                React.createElement("span", {
                  style: { fontSize: "13px", color: C.textDark, flex: 1,
                           minWidth: 0, lineHeight: 1.5,
                           fontWeight: it.is_notable ? 600 : 400 },
                }, it.description),
                it.matter_name && React.createElement("button", {
                  onClick: () => onMatterClick && it.matter_id
                    && onMatterClick({ id: Number(it.matter_id) }),
                  style: {
                    border: "none", background: "transparent", padding: 0,
                    fontSize: "12px", color: C.navy, cursor: "pointer",
                    textDecoration: "underline", flexShrink: 0,
                    maxWidth: "200px", overflow: "hidden",
                    textOverflow: "ellipsis", whiteSpace: "nowrap",
                  },
                }, it.matter_name),
                React.createElement("span", {
                  style: { fontSize: "11px", color: C.textMuted, flexShrink: 0 },
                }, it.user_name),
                React.createElement("span", {
                  style: { fontSize: "11px", color: C.textLight, flexShrink: 0,
                           fontVariantNumeric: "tabular-nums" },
                }, it.time),
              )),
            ),
          )),

          feed.truncated && React.createElement("p", {
            style: { fontSize: "12px", color: C.textMuted, marginTop: "4px" },
          }, "Showing the most recent items only — there is more in this "
           + "window than fits on one page."),
        ),
  );
}

const quietBtn = {
  border: "1px solid #E8E3DC", background: "#fff", borderRadius: "6px",
  padding: "6px 13px", fontSize: "12px", fontWeight: 600,
  color: "#1B2A4A", cursor: "pointer", flexShrink: 0,
};
const primaryBtn = {
  border: "1px solid #1B2A4A", background: "#1B2A4A", borderRadius: "6px",
  padding: "6px 13px", fontSize: "12px", fontWeight: 600,
  color: "#fff", cursor: "pointer", flexShrink: 0,
};
